import {useEffect, useMemo, useRef, useState } from 'react';
import './ChimeMeeting.scss';
import { useGlobalState } from '../../MeetContext/MeetContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../redux/app/hooks';
import { PTA_ROLE, SCHOOL_ADMIN, STUDENT_ROLE, TEACHER_ROLE } from '../../utils/constants';
import images from '../../assets/images';
import { useLazyQuery, useMutation } from '@apollo/client';
import moment from 'moment';
import WarningPopup from '../WarningPopup/WarningPopup';
import { ContentShare, LocalVideo, RosterAttendeeType, useActiveSpeakersState, useAttendeeStatus, useContentShareControls, useContentShareState, useLocalVideo, useMeetingManager, useRosterState } from "amazon-chime-sdk-component-library-react";
import { useChimeMeeting } from '../../Chime/ChimeMeetingContext';
import RecordingTimer from '../Meeting/RecordingTimer/RecordingTimer';
import ChatBox from './MeetingChat/ChatBox/ChatBox';
import Dialogbox from '../Meeting/DialogBox/DialogBox';
import ScreenShareChoosePopup from '../Meeting/ScreenShare/ScreenShareChoosePopup';
import { Avatar } from '@mui/material';
import RemoteVideos from './RemoteVideos';
import ChimeMeetControls from './ChimeMeetControls/ChimeMeetControls';
import ChimeParticipantsListView from './ChimeParticipantsList/ChimeParticipantsListView';
import { ADD_USER_TO_VIDEO_CHAT_ROOM, CREATE_VIDEO_MEET_CHAT_ROOM, GET_UNREAD_MESSAGES_COUNT_FOR_VIDEO_MEET, GET_VIDEO_MEET_CHAT_ROOM } from '../../Chime/helpers/ChatApi/ChatQuery';
import ToasterWithMessage from '../toaster';
import ChimeMeetGroup from './ChimeMeetGroup/ChimeMeetGroup';
import { getInitials } from '../../utils/chimeHelper';
import { InitialiseChatConnection } from '../../websocket/ChatWebSocket';
import { getChimeParticipants } from '../../Chime/helpers/get-participants';

interface ChatMessages {
    id: any;
    message: string;
    userName: string;
    isReadBy: [
        {
            "userName": string,
            "userId": string,
            "attenteeId":string
        }
    ],
  }

  function useDebounceValue<T>(value: T, delay: number): T {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(timer);
        };
    }, [value, delay]);

    return debouncedValue;
    }

    export function useDebouncedActiveSpeakers(delay: number = 500) {
        const activeSpeakers = useActiveSpeakersState();
        const debouncedSpeakers = useDebounceValue(activeSpeakers, delay);

        return debouncedSpeakers;
    }

const ChimeMeeting = () => {
    const { isChatOpen, isParticipantListView, isParticipantsGridViewVisible, toggleChat, toggleParticipant, handleParticipantsGridViewToggle} = useGlobalState();
    const { participantsRef, attendeesDataRef, attendeesData, setAttendeesData, subscribeToHandRaise, isHandRaised, isHost, hostId, attendeeId, isRecording, isRemoteSharing, endMeeting, leaveMeeting,setReadCount } = useChimeMeeting();
    const { role, loginpage, defaultVideoMute, defaultAudioMute } = useAppSelector((state) => state.auth);
    const timeUntilToastRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const timeUntilFinalCallRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const meetComponent = useAppSelector((state) => state?.dashboard?.meetComponent);
    const currentSessionDetail = useAppSelector((state) => state?.dashboard?.currentSessionDetail);
    const [openWarningPopup, setOpenWarningPopup] = useState<boolean>(false);
    const location = useLocation();
    const profile = useAppSelector((state) => state?.auth?.adminProfile);
    const { sharingAttendeeId } = useContentShareState();
    const { toggleContentShare } = useContentShareControls();
    const userName = profile?.getCurrentUser?.nodes[0]?.uFullName;
    const userId = profile?.getCurrentUser?.nodes?.[0]?.uUserName;
    const navigate = useNavigate();
    const {state} = useLocation();    
    const meetingManager = useMeetingManager();
    const {Microphone, MicrophoneOff, ParticipantRaiseHand} = images;
    const {
        muted,
        videoEnabled,
        sharingContent,
    } = useAttendeeStatus(attendeeId ? attendeeId : "");
    const { isVideoEnabled, setIsVideoEnabled } = useLocalVideo();
    const [ localAttendee, setLocalAttendee ] = useState<RosterAttendeeType & { color: string }>({chimeAttendeeId:'',externalUserId:'',name:'', color:''});
    const [chatRoomId, setChatRoomId] = useState('');
    const { roster } = useRosterState();
    const [initialRender, setInitialRender] = useState<boolean>(true);
    const [isReadCount, setIsReadCount] = useState<Boolean>(true);
    const activeSpeakers = useDebouncedActiveSpeakers();
    const [host, setHost] = useState<any>();
    const { error, warning } = ToasterWithMessage();
    const socketRef = useRef<WebSocket | null>(null);
    const pingIntervalRef = useRef<number | null>(null);
    const [chatMessages, setChatMessages] = useState<ChatMessages[]>([]);
    const [socketReadyState, setSocketReadyState] = useState<number>(0);

    const handleSendMessage = (textMessage: string) => {
        console.log("Inside handle message");
        
        const messageData = {
        type: "sendMessage",
        data: {
            userId: userId,
            userName: userName,
            chatRoomId: chatRoomId,
            message: textMessage,
            msgType: "text",
            attenteeId:attendeeId
        },
        };
        console.log('message data', messageData)
        if (socketRef.current) {
        socketRef.current.send(JSON.stringify(messageData));
        }
    };

    const InitialiseChatting = () => {
        socketRef.current = InitialiseChatConnection();
        console.log('socketRef----', socketRef?.current?.readyState)
    };

    const pingFunction = () => {
        console.log('Inside the ping function--------');
        // Send a ping every 30 seconds to keep connection alive
        pingIntervalRef.current = window.setInterval(() => {
        if (socketRef?.current) {
            setSocketReadyState(socketRef?.current?.readyState);
            console.log('interval check---', socketRef?.current?.readyState);
        }
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
            socketRef.current.send(JSON.stringify({ type: 'ping' }));
        }
        }, 1000);
    };

    useEffect(() => {
        InitialiseChatting();

        pingFunction();

        if (!meetingManager?.meetingId) {
            handleNavigate();
        }

        return () => {
        if (pingIntervalRef.current !== null) {
            clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = null;
        }
        if (socketRef.current) {
            socketRef.current.close();
            socketRef.current = null;
        }
        };
    }, []);

    useEffect(()=>{
        if(attendeeId && chatRoomId){
            getUnreadMessageVideoMeetChatRoom({
                variables: {
                    attenteeId: attendeeId,
                    chatroomId: chatRoomId
                }
            });
        }
    },[chatMessages])

    useEffect(() => {
        if (socketReadyState === 0) {
        pingFunction();
        } else if (socketReadyState === 3) {
        InitialiseChatting();
        }
    }, [socketReadyState]);

    useEffect(() => {
        if ((socketRef.current && socketRef.current !== null) && socketRef?.current?.readyState === 1) {
        socketRef.current.onmessage = (event: any) => {
            const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
            console.log('Data----', data)
            switch (data.type) {
            case "getChatMessages":
                if (data?.response?.status === 200) {
                let getChatMessages = data?.response?.chats?.chatData;
                console.log(data.response.chats.chatData, '======getMessagesData');
                if(getChatMessages && getChatMessages.length > 0){
                    getChatMessages = getChatMessages?.reverse();
                    setChatMessages(prevMessages => [
                    ...prevMessages,
                    ...getChatMessages
                    ]);
                }
                }
                break;
            case "newMessageAdded":
                const newChatData = data?.data;
                if(newChatData?.chatRoomId === chatRoomId){
                setChatMessages(prevMessages => [
                    ...prevMessages,
                    newChatData
                ]);
                console.log('newMessageAdded', data.data);
                }
                break;

                case "refetchChatMessages":
                    console.log("Inside refetch Messages");
                    
                    if(chatRoomId && attendeeId){
                        getUnreadMessageVideoMeetChatRoom({
                            variables: {
                                attenteeId: attendeeId,
                                chatroomId: chatRoomId
                            }
                        });
                    }
                break;
            default:
                break;
            }
        }
        if (socketRef?.current?.readyState === 1) {
            const messageData = {
            "type": "getAllMessagesByChatRoomId",
            "data": {
                "chatRoomId": chatRoomId,
                "exclusiveStartKey": '',
                "limit": null
            }
            }
            socketRef.current.onerror = (event) => {
            console.log('ONERROR', event)
            }
            try {
            console.log(messageData, '====for getchatmessages');

            socketRef.current.send(JSON.stringify(messageData));
            } catch (err) {
            console.log('err in socket-----', err)
            }
        }
        }
    }, [socketRef.current?.readyState,chatRoomId]);


    useEffect(()=>{
        const filteredData = chatMessages?.filter(chat => !chat.isReadBy.some((reader: { attenteeId: any; }) => reader.attenteeId === attendeeId)).map(chat => chat?.id);

        console.log("filteredData",filteredData);
        
        const unreadMessageData = {
        type: "updateUnreadMessagesForVideoMeet",
        data: {
            messageIds: filteredData || [],
            chatRoomId: chatRoomId,
            userId: userId,
            userName: userName,
            attenteeId:attendeeId
        }
        };
        if (unreadMessageData.data.messageIds.length > 0 && isChatOpen) {
        if (socketRef.current && socketRef.current.readyState === 1) {
            socketRef.current.send(JSON.stringify(unreadMessageData));
        }
        }
    },[chatMessages,isChatOpen])

    const toggleCamera = async () => {
        if (isVideoEnabled || !meetingManager.selectedVideoInputDevice) {
            meetingManager.meetingSession?.audioVideo?.stopLocalVideoTile();
            // Change the state to hide the `LocalVideo` tile
            setIsVideoEnabled(false);
        } else {
            await meetingManager.meetingSession?.audioVideo?.startVideoInput(meetingManager.selectedVideoInputDevice);
            meetingManager.meetingSession?.audioVideo?.startLocalVideoTile();
            // Change the state to display the `LocalVideo` tile
            setIsVideoEnabled(true);
        }
    };

    const updateAudioVideoOnDeviceChange = async () => {
        const meetingAudioVideo = meetingManager?.meetingSession?.audioVideo;
        meetingAudioVideo?.addDeviceChangeObserver({
            async audioInputsChanged() {
                const audioInputDevices = await meetingAudioVideo?.listAudioInputDevices();
                meetingManager.startAudioInputDevice(audioInputDevices[0].deviceId);
            },
            async audioOutputsChanged() {
                const audioInputDevices = await meetingAudioVideo?.listAudioOutputDevices();
                meetingManager.startAudioOutputDevice(audioInputDevices[0].deviceId);
            },
            async videoInputsChanged() {
                const audioInputDevices = await meetingAudioVideo?.listVideoInputDevices();
                meetingManager.startVideoInputDevice(audioInputDevices[0].deviceId);
            },
        });
    };

    useEffect( () => {
        subscribeToHandRaise();
        console.log("[Log] default audio and video", { defaultAudioMute, defaultVideoMute });
        if(meetingManager?.meetingId && !videoEnabled && !defaultVideoMute) {
            toggleCamera();
            console.log("[Log] defaultVideoMute", defaultVideoMute);
        }
        updateAudioVideoOnDeviceChange();
    },[]);

    useEffect( () => {
        if(roster && attendeeId){
            const attendeeData = roster[attendeeId] as RosterAttendeeType & { color: string };
            if (attendeeData) {
            // Set local attendee data with a color property
                setLocalAttendee({
                    ...attendeeData,
                    color: attendeeData?.color || '#bdbdbd'
                });
            }
            const attendeeKeys = Object.keys(roster);
            let temp :any[] = [];
            let partTemp: any = {};

            const filteredAttendeeKeys: string[] = attendeeKeys.filter(
                (attendeeId) => !activeSpeakers.includes(attendeeId)
            );

            const finalActiveSpeakers: string[] = activeSpeakers.map(
                (attendeeId) => attendeeId?.split('#')?.[0]
            );

            let finalAttendeeKeys: string[] = Array.from(new Set([...finalActiveSpeakers, ...filteredAttendeeKeys]));
            console.log("[Activespeakers] ---------------");
            console.log("[Activespeakers] activeSpeakers", activeSpeakers);
            console.log("[Activespeakers] finalActiveSpeakers", finalActiveSpeakers);
            console.log("[Activespeakers] attendeeKeys", attendeeKeys);
            console.log("[Activespeakers] filteredAttendeeKeys", filteredAttendeeKeys);
            console.log("[Activespeakers] finalAttendeeKeys", finalAttendeeKeys);
            console.log("[Activespeakers] ---------------");
            console.log("[Activespeakers] ----------> ", { activeSpeakers, attendeeKeys, finalActiveSpeakers, filteredAttendeeKeys, finalAttendeeKeys });
            finalAttendeeKeys.map( (id: any ) => {
                const chimeId = id?.split('#')?.[0];
                const attendeeData = roster[chimeId] as RosterAttendeeType & { color: string } || {};
                partTemp[chimeId] = {attendeeData, handRaised: participantsRef.current[chimeId]?.handRaised ?? false};
                console.log('attendee data inside the loop=====>',attendeeData);

                if(attendeeData?.chimeAttendeeId !== attendeeId && !attendeeData?.externalUserId?.includes("aws:MediaPipeline") && attendeeData?.externalUserId &&  attendeeData?.chimeAttendeeId && attendeeData?.name){
                    temp.push({
                        chimeAttendeeId : attendeeData?.chimeAttendeeId,
                        externalUserId: attendeeData?.externalUserId,
                        name: attendeeData?.name,
                        handRaised: participantsRef.current[attendeeData?.chimeAttendeeId]?.handRaised ?? false,
                        color: attendeeData?.color || '#bdbdbd'
                    });
                }
            });
            participantsRef.current = partTemp;
            setAttendeesData(temp);
            attendeesDataRef.current = temp;
        }
    }, [roster, activeSpeakers]);

    const handleNavigate = () => {
        if (role === STUDENT_ROLE) {
            navigate('/student-classroom/liveclass', { state: state });
        } else if (role === TEACHER_ROLE && meetComponent === 'liveclass') {
            navigate('/teacher-classroom/liveclass', { state: state });
        } else if (role === TEACHER_ROLE && meetComponent === 'workshop') {
            navigate('/teacher-workshop');
        } else if (role === TEACHER_ROLE && meetComponent === 'pta') {
            navigate('/teacher-pta', { state: state });
        } else if (((role === SCHOOL_ADMIN && loginpage === SCHOOL_ADMIN)) && meetComponent === 'pta') {
            navigate('/admin-pta/meetings', { state: state });
        } else if (((role === SCHOOL_ADMIN && loginpage === SCHOOL_ADMIN)) && meetComponent === 'workshop') {
            navigate('/admin-workshop');
        } else if (role === PTA_ROLE) {
            navigate('/pta-meetings', { state: state });
        }
    }
    
    const checkEndTime = async() => {
        console.log('checkEndTime=====>',isHost);
        if(isHost){
            try {
                await endMeeting();
            } catch (error) {
                console.error('Failed to end meeting:', error);
                alert('An error occurred while end the meeting. Please try again.');
            }
        }else{
            try {
                await leaveMeeting();
            } catch (error) {
                console.error('Failed to leave meeting:', error);
                alert('An error occurred while leaving the meeting. Please try again.');
            }
        }
        toggleChat(false);
        toggleParticipant(false);
        handleParticipantsGridViewToggle(false);
        handleNavigate();
    };

    const [ createVideoMeetChatRoom ] = useMutation(CREATE_VIDEO_MEET_CHAT_ROOM, {
        fetchPolicy: "no-cache",
        onCompleted: (data) => {
            console.log(data?.createChatRoomForVideoMeet,'=====data');
            setChatRoomId(data.createChatRoomForVideoMeet);
            getUnreadMessageVideoMeetChatRoom({
                variables: {
                    attenteeId: attendeeId,
                    chatroomId: data.createChatRoomForVideoMeet
                }
            })   
        },
        onError: (err: any) => {
          console.log(err?.message,'=====Error in create video meet chat room');
          error(err?.message);
        }
    });

    const [ addUserToVideoChatRoom ] = useMutation(ADD_USER_TO_VIDEO_CHAT_ROOM, {
        fetchPolicy: "no-cache",
        onCompleted: (data) => {
            console.log(data,'=====data in addUserToVideoChatRoom');
            console.log("attendeeId",attendeeId);
            
            getUnreadMessageVideoMeetChatRoom({
                variables: {
                    attenteeId: attendeeId,
                    chatroomId: chatRoomId
                }
            });
        },
        onError: (err: any) => {
            console.log(err?.message,'=====Error in create video meet chat room');

            if(err.message === "Error adding user to chat room"){
                getUnreadMessageVideoMeetChatRoom({
                    variables: {
                        attenteeId: attendeeId,
                        chatroomId: chatRoomId
                    }
                });
            }
        }
    });

    const [ getVideoMeetChatRoom ] = useLazyQuery(GET_VIDEO_MEET_CHAT_ROOM, {
        fetchPolicy: "network-only",
        onCompleted: async (data) => {
            setChatRoomId(data?.allVideoMeetChats?.nodes[0]?.chatroomId);
            console.log(attendeeId,'=====attendeeId');
            console.log(data?.allVideoMeetChats?.nodes[0]?.chatroomId,'=====GET_VIDEO_MEET_CHAT_ROOM');
            //@ts-ignore
            const getAttentees  = await getChimeParticipants(meetingManager?.meetingId);

            const filteredAtteenteeData = getAttentees?.Attendees?.filter( 
                (attendeeId) => attendeeId?.ExternalUserId !== hostId
            )
            
            //@ts-ignore
            if(isHost && filteredAtteenteeData?.length === 0 && !data?.allVideoMeetChats?.nodes[0]?.chatroomId && location?.state?.sessionId){
                console.log('creating video chat room');
                
                createVideoMeetChatRoom({
                    variables: {
                        attenteeId: attendeeId,
                        meetId: location?.state?.sessionId
                    }
                })   
            }

            if(data?.allVideoMeetChats?.nodes[0]?.chatroomId && attendeeId)
            addUserToVideoChatRoom({
                variables: {
                    attenteeId: attendeeId,
                    chatroomId: data?.allVideoMeetChats?.nodes[0]?.chatroomId
                } 
            })
        },
        onError: (err: any) => {
          error(err?.message);
        }
    });

    const [ getUnreadMessageVideoMeetChatRoom ] = useLazyQuery(GET_UNREAD_MESSAGES_COUNT_FOR_VIDEO_MEET, {
        fetchPolicy: "network-only",
        onCompleted: (data) => {
           console.log("+++Unread Count",data?.getUnreadMessagesForVideoChat);
           setReadCount(data?.getUnreadMessagesForVideoChat) 
        },
        onError: (err: any) => {
          error(err?.message);
        }
    });
    
    useEffect(() => { 
        console.log(isHost,'=====isHost');
        console.log(attendeesData?.length,'====attendeesData?.length');
            if(location?.state?.sessionId){
                getVideoMeetChatRoom({
                    variables: {
                        meetId: location?.state?.sessionId
                    }
                })
           }
        },[attendeeId])


    useEffect(()=>{

        if(attendeeId && chatRoomId){
            getUnreadMessageVideoMeetChatRoom({
                variables: {
                    attenteeId: attendeeId,
                    chatroomId: chatRoomId
                }
            });
        }
        
    },[isReadCount,attendeeId,chatRoomId])
    
    useEffect(() => {
        // Parse start and end times
        const start = moment(currentSessionDetail.startDate);
        const end = moment(currentSessionDetail.endDate);
    
        // Calculate duration in minutes
        const duration = moment.duration(end.diff(start)).asMinutes();
    
        let adjustedEndTime;
    
        if (duration <= 5) {
          adjustedEndTime = end.clone().subtract(1, 'minute');
        } else {
          adjustedEndTime = end.clone().subtract(5, 'minutes');
        }
    
        // Calculate the times for the toaster and final method
        const currentTime = moment();
        const timeUntilToast = adjustedEndTime.diff(currentTime);
        const timeUntilFinalCall = end.clone().add(1, 'minute').diff(currentTime);
    
        // Set timeout for showing toaster
        if (timeUntilToast > 0) {
          timeUntilToastRef.current = setTimeout(() => {
            if((role === SCHOOL_ADMIN && meetComponent === 'workshop') || (role === TEACHER_ROLE && meetComponent === 'liveclass')){
                setOpenWarningPopup(true);
            }
          }, timeUntilToast);
        }
    
        // Set timeout for calling the final method
        if (timeUntilFinalCall > 0) {
          timeUntilFinalCallRef.current = setTimeout(() => {
            console.log("timeUntilFinalCall", timeUntilFinalCall);
            checkEndTime();
          }, timeUntilFinalCall);
        }

        return () => {
            if(timeUntilToastRef.current){
                clearTimeout(timeUntilToastRef.current);
            }
            if(timeUntilFinalCallRef.current){
                clearTimeout(timeUntilFinalCallRef.current);
            }
        }
      }, [currentSessionDetail?.startDate, currentSessionDetail?.endDate, checkEndTime]);


      const closeWarnPopup  = () => {
        setOpenWarningPopup(false);
      }

    useEffect(() => {
        if(meetingManager?.meetingStatus === 3 || meetingManager?.meetingStatus === 4 || meetingManager?.meetingStatus === 5){
            if (meetingManager?.meetingStatus === 3) {
                warning("Meeting has ended");
            }
            if (meetingManager?.meetingStatus === 4) {
                warning("You have joined from another device.");
            }
            setTimeout(() => {
            if(role && meetComponent){
                toggleChat(false);
                toggleParticipant(false);
                handleParticipantsGridViewToggle(false);
                if(role === STUDENT_ROLE){
                    navigate('/student-classroom/liveclass',{state:state});
                }else if(role === TEACHER_ROLE && meetComponent === 'liveclass'){
                    navigate('/teacher-classroom/liveclass',{state:state});
                }else if(role === TEACHER_ROLE && meetComponent === 'workshop'){
                    navigate('/teacher-workshop');
                }else if(role === TEACHER_ROLE && meetComponent === 'pta'){
                    navigate('/teacher-pta', {
                        state: location?.state
                      });
                }else if(((role === SCHOOL_ADMIN && loginpage === SCHOOL_ADMIN)) && meetComponent === 'pta'){
                    navigate('/admin-pta/meetings', {
                        state: location?.state
                      });
                }else if(((role === SCHOOL_ADMIN && loginpage === SCHOOL_ADMIN)) && meetComponent === 'workshop'){
                    navigate('/admin-workshop');
                }else if(role === PTA_ROLE) {
                    navigate('/pta-meetings', {
                        state: {
                            category: location?.state?.category
                        }
                      });
                }
           }
            }, 2000)
        }
    }, [meetingManager?.meetingStatus])

    useEffect(()=>{
       console.log("isRecording=======>",isRecording)
    },[isRecording]);

    useEffect(() => {
        setHost(attendeesData.find((participant: any) => participant?.externalUserId === hostId));
    }, [attendeesData]);


    const handleStopShareScreen = () => {
        if (sharingContent) {
            toggleContentShare();
        }
    }

    console.log("participants", videoEnabled, isVideoEnabled);
    console.log('attendees data',attendeesData,'roaster--------->',roster);
    console.log('meetingmanager', meetingManager);
    console.log('meetingmanager meetingStatus', meetingManager?.meetingStatus);
    console.log("handraise", isHandRaised);
    console.log("isRemotesharing======>",isRemoteSharing);
    console.log("sharingAttendeeId=====>",sharingAttendeeId, sharingAttendeeId ? roster[sharingAttendeeId.split('#')?.[0]]?.name : "");
    console.log("localAttendee====>",localAttendee);
    console.log("host", host);

    const memoizedAttendees = useMemo(() => attendeesData, [attendeesData]);

    useEffect(() => {
        console.log("[Log] memoizedAttendees", memoizedAttendees);
    }, [memoizedAttendees])

    return(
        <>
        {isParticipantsGridViewVisible ? (
            <ChimeMeetGroup localAttendee={localAttendee}/>
        ) : (
        <>
        <div className='videoMainContainerChime' style={{flexDirection: (sharingContent || sharingAttendeeId) ? 'column-reverse' : 'column'}}>
            {(attendeesData.length > 0 || (attendeesData?.length === 0 && sharingContent) || (!isHost && host)) &&
                <div className='OthersVideoContainer' style={{padding: (sharingContent || sharingAttendeeId) ? '5px 10px 10px 10px' : ''}}>
                    {(sharingContent || sharingAttendeeId || (!isHost && host)) && (
                        <div className="remote-tile-innerContainer">
                            <div className="remote-tile-icon">
                                <div className="micIconContainer">
                                    <img src={ (muted) ? MicrophoneOff : Microphone } className={"micIcon"} alt="videoOn" />
                                </div>
                                <div className="handIconContainer">
                                    <img src={ParticipantRaiseHand} className={isHandRaised ? 'raiseHandStyle' : 'raiseHandHidden'} alt="videoOn"/>
                                </div>
                            </div>
                                {videoEnabled ? (
                                    <>
                                        <LocalVideo className='local-video-tile'  />
                                        <div className="participantName">
                                            <span style={{ textShadow: "0 1px 2px rgba(0,0,0,.6),0 0 2px rgba(0,0,0,.3)", letterSpacing: "1px" }}>
                                                {localAttendee?.name ?
                                                    `${(localAttendee?.name)?.charAt(0).toUpperCase() + (localAttendee?.name)?.slice(1)} ${localAttendee.externalUserId === hostId ? `(Host)` : ''}`
                                                    : ""}
                                            </span>
                                        </div>
                                    </>) : (
                                    <div style={{display:'flex', justifyContent:'center', alignItems:'center', backgroundColor: 'black'}} className="remote-video">
                                        <Avatar sx={{ width: 80, height: 80, fontSize: 24, fontWeight:600, zIndex: 1 , backgroundColor:localAttendee?.color}}>{getInitials(localAttendee?.name)}</Avatar>
                                        <div className="participantName">
                                            <span style={{ textShadow: "0 1px 2px rgba(0,0,0,.6),0 0 2px rgba(0,0,0,.3)", letterSpacing: "1px" }}>
                                                {localAttendee?.name ?
                                                    `${(localAttendee?.name)?.charAt(0).toUpperCase() + (localAttendee?.name)?.slice(1)} ${localAttendee.externalUserId === hostId ? `(Host)` : ''}`
                                                    : ""}
                                            </span>
                                        </div>
                                    </div>
                                )}
                        </div>
                    )}
                    {((!isHost && host) && (sharingAttendeeId || sharingContent)) && (
                        <RemoteVideos Attendee={host} key={host?.chimeAttendeeId} hand={host.handRaised} isMainTile={false}/>
                    )}
                    {memoizedAttendees.map( (attendee: any, index) =>  {
                        if (attendee?.externalUserId !== hostId) {
                            return <RemoteVideos Attendee={attendee} key={attendee.externalUserId ?? attendee.chimeAttendeeId} hand={attendee.handRaised} isMainTile={false}/>
                        }
                    })}
                </div> 
            }
            <div className='localVideoContainer'>
                <div className='localVideoTile'>
                {(isRecording) && <RecordingTimer initialRender={initialRender} setInitialRender={setInitialRender}/>}
                {(sharingAttendeeId && !sharingContent) ? (
                    <div className="share-container">
                        <ContentShare nameplate={`${roster[sharingAttendeeId.split('#')?.[0]]?.name} is presenting`} className='local-share'/>
                    </div>
                ) : (
                    sharingContent ? (
                        <>
                            <div className='stopScreenShareContainer'>
                                <div className='sharedPersonName'>{"You are presenting"}</div>
                                <div className='stopShareElement' onClick={handleStopShareScreen}>
                                    <div className='stopIconStyle'></div>
                                    <div className='stopTextStyle'>{`Stop`}</div>
                                </div>
                            </div>
                            <ContentShare nameplate='You are presenting' className='local-share'/>
                        </>
                    ) : ((!isHost && host) ? (
                        <RemoteVideos Attendee={host} key={host?.chimeAttendeeId} hand={host.handRaised} isMainTile={true}/>
                    ) : (
                        <div className='localTileContainer'>
                            <div className='iconsContainer'>
                                <div className="micIconContainer">
                                    <img src={ (muted) ? MicrophoneOff : Microphone } className={"micIcon"} alt="videoOn" />
                                </div>
                                <div className="handIconContainer">
                                    <img src={ParticipantRaiseHand} className={isHandRaised ? 'raiseHandStyle' : 'raiseHandHidden'} alt="videoOn"/>
                                </div>
                            </div>
                            {
                            videoEnabled ? (
                                <div className='local-tile'>
                                    <LocalVideo className='local-video-tile' />
                                    <div className="participantName">
                                        <span style={{ textShadow: "0 1px 2px rgba(0,0,0,.6),0 0 2px rgba(0,0,0,.3)", letterSpacing: "1px" }}>
                                            {localAttendee?.name ?
                                                `${(localAttendee?.name)?.charAt(0).toUpperCase() + (localAttendee?.name)?.slice(1)} ${localAttendee.externalUserId === hostId ? `(Host)` : ''}`
                                                : ""}
                                        </span>
                                    </div>
                                </div>) :
                                <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', borderRadius: "20px", backgroundColor: 'black' }}>
                                    <Avatar sx={{ fontSize: '100px', height: '200px', width: '200px', backgroundColor: localAttendee?.color }} >{getInitials(localAttendee?.name)}</Avatar>
                                    <div className="participantName">
                                        <span style={{ textShadow: "0 1px 2px rgba(0,0,0,.6),0 0 2px rgba(0,0,0,.3)", letterSpacing: "1px" }}>
                                            {localAttendee?.name ?
                                                `${(localAttendee?.name)?.charAt(0).toUpperCase() + (localAttendee?.name)?.slice(1)} ${localAttendee.externalUserId === hostId ? `(Host)` : ''}`
                                                : ""}
                                        </span>
                                    </div>
                                </div>
                            }
                        </div>
                    )
                )
                )
                }
                {
                    !(sharingContent || sharingAttendeeId) && 
                    <div className='meetControls'>
                        <ChimeMeetControls/>
                    </div>
                }
                </div>
                {(isChatOpen) && (
                    <div className='chatContainer'>
                        <ChatBox handleSendMessage = {handleSendMessage} chatMessages = {chatMessages} currentUserName={localAttendee?.name}/>
                    </div>
                )}
                {(isParticipantListView) && (
                    <div className='chatContainer'>
                        <ChimeParticipantsListView />
                    </div>
                )}
            </div>
            {
                (sharingContent || sharingAttendeeId) && 
                <div className='meetControlsOutside'>
                    <ChimeMeetControls/>
                </div>
            }
        </div>
        </>
        )}
        <Dialogbox />
        <WarningPopup
            message='The meeting will be automatically ended on scheduled time'
            openWarningPopup={ openWarningPopup }
            closeWarnPopup={ closeWarnPopup }
        />
        <ScreenShareChoosePopup />
        </>
    )
}


export default ChimeMeeting;